export type SidePanelContentMode = 'push' | 'overlay'

const STORAGE_KEY = 'backoffice.sidePanelContentMode'
const CHANGE_EVENT = 'sidePanelContentModeChange'

// The header toggle is opt-in per app: only apps whose layout reacts to the
// preference (voyage's DefaultTemplate) should surface it. Call once at bootstrap.
let contentModeToggleEnabled = false

export const enableSidePanelContentModeToggle = () => {
  contentModeToggleEnabled = true
}

export const isSidePanelContentModeToggleEnabled = () => contentModeToggleEnabled

// Per-user preference for how side panels interact with the page content:
// 'push' squeezes the content to make room for the panel, 'overlay' renders
// the panel on top of it. Persisted in localStorage, shared by all side panels.
export const getSidePanelContentMode = (): SidePanelContentMode => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'overlay' ? 'overlay' : 'push'
  } catch {
    return 'push'
  }
}

export const setSidePanelContentMode = (mode: SidePanelContentMode) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    // Preference simply won't persist when storage is unavailable
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

export const toggleSidePanelContentMode = (): SidePanelContentMode => {
  const next: SidePanelContentMode = getSidePanelContentMode() === 'push' ? 'overlay' : 'push'
  setSidePanelContentMode(next)
  return next
}

// Fires on same-tab changes (custom event) and cross-tab changes (storage event).
// Returns an unsubscribe function.
export const subscribeSidePanelContentMode = (
  callback: (mode: SidePanelContentMode) => void,
): (() => void) => {
  const handler = () => callback(getSidePanelContentMode())

  window.addEventListener(CHANGE_EVENT, handler)
  window.addEventListener('storage', handler)

  return () => {
    window.removeEventListener(CHANGE_EVENT, handler)
    window.removeEventListener('storage', handler)
  }
}
